import { json } from "@remix-run/node";
import type { LoaderFunctionArgs, MetaFunction } from "@remix-run/node";
import { useOutletContext } from "@remix-run/react";
import type { Locale } from "~/utils/locale";
import { detectLocale, localePath } from "~/utils/locale";
import { t } from "~/utils/translations";
import { Breadcrumbs } from "~/components/storefront/Breadcrumbs";
import { buildPageMeta, originFromRequest } from "~/utils/storefrontMeta";

export const loader = async ({ request }: LoaderFunctionArgs) => {
  return json(
    { locale: detectLocale(request.url), origin: originFromRequest(request) },
    {
      headers: {
        "Cache-Control": "public, max-age=3600, s-maxage=3600, stale-while-revalidate=86400",
      },
    },
  );
};

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  const locale = (data?.locale || "vi") as Locale;
  const origin = data?.origin || "https://diolo.com";
  return buildPageMeta({
    title: locale === "vi" ? "Về Diolo" : "About — Diolo",
    description:
      locale === "vi"
        ? "Diolo — trang phục pickleball nam, thiết kế tại Sài Gòn. Câu chuyện phía sau DIOLO 001."
        : "Diolo — men's pickleball wear, designed in Saigon. The story behind DIOLO 001.",
    path: localePath("/about", locale),
    origin,
    locale,
  });
};

export default function AboutPage() {
  const { locale } = useOutletContext<{ locale: Locale }>();

  return (
    <div className="mx-auto max-w-[1440px] px-4 sm:px-6 lg:px-8 py-12">
      <Breadcrumbs locale={locale} items={[{ label: t("about", locale) }]} />
      {/* Hero */}
      <div className="text-center mb-16">
        <p className="text-xs uppercase tracking-widest text-luxury-stone mb-4">
          {locale === "vi" ? "Câu chuyện thương hiệu" : "Our Story"}
        </p>
        <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-light text-luxury-charcoal tracking-normal">
          Diolo
        </h1>
        <p className="mt-6 max-w-2xl mx-auto text-luxury-stone text-sm sm:text-base leading-relaxed">
          {locale === "vi"
            ? "Diolo làm trang phục pickleball cho nam giới. Thiết kế tại Sài Gòn, cắt cho sân đấu, và để mặc tiếp sau điểm cuối cùng — vì môn thể thao này xứng đáng hơn những gì đang khoác trên người chúng ta."
            : "Diolo makes pickleball wear for men. Designed in Saigon, cut for the court, and built to be worn long after the last point — because the sport is better than what's on our backs."}
        </p>
      </div>

      {/* Brand Image */}
      <div className="mb-16">
        <div className="aspect-[21/9] rounded-sm overflow-hidden">
          <img
            src="/editorial/night-court.jpg"
            alt="Diolo — the night court"
            className="w-full h-full object-cover"
          />
        </div>
      </div>

      {/* Mission */}
      <div className="max-w-3xl mx-auto text-center mb-20">
        <h2 className="font-display text-xl sm:text-2xl font-light text-luxury-charcoal tracking-normal mb-6">
          {locale === "vi" ? "Vì sao có Diolo" : "Why Diolo"}
        </h2>
        <p className="text-luxury-stone text-sm sm:text-base leading-relaxed">
          {locale === "vi"
            ? "Pickleball là môn thể thao phát triển nhanh nhất thế giới, nhưng những người đàn ông chơi nó vẫn mặc đồ tập thừa lại. Diolo ra đời để cho môn thể thao này một góc nhìn: quần áo có gu và có cá tính, để thi đấu và để sống cùng. Bốn kiểu dáng để bắt đầu — DIOLO 001. Mọi thứ sau đó đều xây trên nền này."
            : "Pickleball is the fastest-growing game in the world, and the men playing it are still dressed in gym leftovers. We started Diolo to give the sport a point of view: clothes with taste and edge, made to compete in and to live in. Four styles to begin with — DIOLO 001. Everything after builds on it."}
        </p>
      </div>

      {/* Values */}
      <div className="mb-20">
        <h2 className="font-display text-xl sm:text-2xl font-light text-luxury-charcoal tracking-normal text-center mb-12">
          {locale === "vi" ? "Điều chúng tôi theo đuổi" : "What we stand for"}
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
          {[
            {
              title: locale === "vi" ? "Sân đấu trước hết" : "Court first",
              desc: locale === "vi"
                ? "Mỗi sản phẩm được làm để chơi thật — chất liệu thoáng và co giãn qua trọn trận đấu, đường may đặt ở nơi cú vung vợt không chạm tới."
                : "Every piece is made to be played in — fabrics that move and breathe through a full match, seams placed where a paddle swing won't feel them.",
              icon: (
                <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12.75L11.25 15 15 9.75M21 12c0 1.268-.63 2.39-1.593 3.068a3.745 3.745 0 01-1.043 3.296 3.745 3.745 0 01-3.296 1.043A3.745 3.745 0 0112 21c-1.268 0-2.39-.63-3.068-1.593a3.746 3.746 0 01-3.296-1.043 3.746 3.746 0 01-1.043-3.296A3.745 3.745 0 013 12c0-1.268.63-2.39 1.593-3.068a3.745 3.745 0 011.043-3.296 3.746 3.746 0 013.296-1.043A3.746 3.746 0 0112 3c1.268 0 2.39.63 3.068 1.593a3.746 3.746 0 013.296 1.043 3.746 3.746 0 011.043 3.296A3.745 3.745 0 0121 12z" />
                </svg>
              ),
            },
            {
              title: locale === "vi" ? "Thiết kế, không mua sẵn" : "Designed, not sourced",
              desc: locale === "vi"
                ? "Cắt và hoàn thiện tại Sài Gòn theo rập riêng, chỉnh từng centimet trên chính những người chơi. Không có gì lấy từ catalogue."
                : "Cut and finished in Saigon from our own patterns, fitted by the centimetre on the people who play. Nothing off a catalogue.",
              icon: (
                <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM14.06 6.19l2.12-2.12a1.5 1.5 0 012.12 0l1.63 1.63a1.5 1.5 0 010 2.12l-2.12 2.12M6 15l3 3" />
                </svg>
              ),
            },
            {
              title: locale === "vi" ? "Born to Compete" : "Born to Compete",
              desc: locale === "vi"
                ? "Tiết chế hơn ồn ào. Đen, navy, xám. Một tủ đồ đúng chỗ ở lưới lúc 9 giờ tối và ở bàn ăn lúc 11 giờ."
                : "Restraint over noise. Black, navy, grey. A wardrobe that looks right at the net at 9 pm and at dinner at 11.",
              icon: (
                <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z" />
                </svg>
              ),
            },
          ].map((value) => (
            <div key={value.title} className="text-center p-6">
              <div className="w-14 h-14 mx-auto mb-5 rounded-full bg-luxury-cream flex items-center justify-center text-luxury-charcoal">
                {value.icon}
              </div>
              <h3 className="font-display text-sm uppercase tracking-normal text-luxury-charcoal mb-3">
                {value.title}
              </h3>
              <p className="text-luxury-stone text-sm leading-relaxed">
                {value.desc}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Team Photo */}
      <div className="max-w-3xl mx-auto">
        <div className="aspect-[16/9] rounded-sm overflow-hidden mb-6">
          <img
            src="/editorial/night-fence.jpg"
            alt={locale === "vi" ? "Diolo, Thành phố Hồ Chí Minh" : "Diolo, Ho Chi Minh City"}
            className="w-full h-full object-cover"
          />
        </div>
        <p className="text-center text-luxury-stone text-sm leading-relaxed">
          {locale === "vi"
            ? "Diolo được làm tại Thành phố Hồ Chí Minh, bởi những người chơi pickleball."
            : "Diolo is made in Ho Chi Minh City, by people who play."}
        </p>
      </div>
    </div>
  );
}
